import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  ConstrainMode,
  DetailsListLayoutMode,
  DetailsRow,
  IDetailsHeaderProps,
  IRenderFunction,
  mergeStyleSets,
  ScrollablePane,
  SelectionMode,
  ShimmeredDetailsList,
  Sticky,
  StickyPositionType,
  TooltipHost,
  IColumn,
  TooltipOverflowMode,
} from "@fluentui/react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { TableSpinner } from "../../components/TableSpinner";
import { useSelector } from "../../store/hooks";
import {
  selectShownReports,
  setSortReports,
} from "../../store/reports-page/reportsPage";
import { loadReportsActions } from "../../store/reports/reports";
import { AppDispatch } from "../../store/store";
import { PasteDialog } from "../recommendations/PasteDialog";
import { columnFilterConfigs, getColumnOptionsColumns } from "./columns";
import { Navigation } from "./Navigation";
import { ChangeLocationDialog } from "./ChangeLocationDialog";
import { DeleteDialog } from "./DeleteDialog";
import {
  useTableSort,
} from "../../components/table";
import { useColumnWidths } from "../../hooks/useColumnWidths";
import { ColumnOptions, useColumnOptionsState } from "../../components/column-options";
import { formatNumber } from "../../utils/number";
import { formatDateString } from "../../utils/utils";
import { ContextButton } from "./ContextButton";
import { ActivateDeactivateCell } from "./ActivateDeactivateCell";
import {
  CenteredCell,
  ListContextMenuIconButton,
} from "../../components/styled";
import { TooltipComponent } from "components/TooltipComponent";
import { useTheme } from "styled-components";
import { IStyledTheme } from "../../theme/types";
import { Report } from "../../types/types";
import { ExcelExport } from "@progress/kendo-react-excel-export";
import { ExportExcelGeneric } from "../../components/ExportExcelGeneric";
import moment from "moment";

// Define columns in Fluent UI format
const defaultColumns = [
  {
    key: "reportName",
    fieldName: "reportName",
    labelKey: "ram.reportList.reportName",
    minWidth: 150,
    maxWidth: 300,
    isResizable: true,
    isSortable: true,
  },
  {
    key: "options",
    fieldName: "options",
    labelKey: "",
    minWidth: 40,
    maxWidth: 40,
    isResizable: false,
    isSortable: false,
  },
  {
    key: "clientName",
    fieldName: "clientName",
    labelKey: "ram.clientsList.clientName",
    minWidth: 150,
    maxWidth: 300,
    isResizable: true,
    isSortable: true,
  },
  {
    key: "webBasePartnerNumber",
    fieldName: "webBasePartnerNumber",
    labelKey: "ram.clientsList.clientId",
    minWidth: 150,
    maxWidth: 300,
    isResizable: true,
    isSortable: true,
  },
  {
    key: "locationName",
    fieldName: "locationName",
    labelKey: "ram.locationList.locationName",
    minWidth: 150,
    maxWidth: 300,
    isResizable: true,
    isSortable: true,
  },
  {
    key: "surveyDate",
    fieldName: "surveyDate",
    labelKey: "ram.reportList.surveyDate",
    minWidth: 100,
    maxWidth: 150,
    isResizable: true,
    isSortable: true,
  },
  {
    key: "pmlinMioEur",
    fieldName: "pmlinMioEur",
    labelKey: "ram.locationList.pmlinMioEur",
    minWidth: 100,
    maxWidth: 150,
    isResizable: true,
    isSortable: true,
  },
  {
    key: "emlinMioEur",
    fieldName: "emlinMioEur",
    labelKey: "ram.locationList.emlinMioEur",
    minWidth: 100,
    maxWidth: 150,
    isResizable: true,
    isSortable: true,
  },
  {
    key: "insurersRaiting",
    fieldName: "insurersRaiting",
    labelKey: "ram.locationList.insurersRating",
    minWidth: 100,
    maxWidth: 150,
    isResizable: true,
    isSortable: true,
  },
  {
    key: "reportCreatorCode",
    fieldName: "reportCreatorCode",
    labelKey: "ram.reportList.reportCreatorCode",
    minWidth: 100,
    maxWidth: 150,
    isResizable: true,
    isSortable: true,
  },
  {
    key: "isReportDeactivated",
    fieldName: "isReportDeactivated",
    labelKey: "ram.reportList.isActive",
    minWidth: 120,
    maxWidth: 200,
    isResizable: true,
    isSortable: true,
  },
];

const allReportExcelColumns = [
  { key: "reportName", labelKey: "ram.reportList.reportName", width: 200 },
  { key: "clientName", labelKey: "ram.clientsList.clientName", width: 200 },
  {
    key: "webBasePartnerNumber",
    labelKey: "ram.clientsList.clientId",
    width: 150,
  },
  {
    key: "locationName",
    labelKey: "ram.locationList.locationName",
    width: 200,
  },
  { key: "surveyDate", labelKey: "ram.reportList.surveyDate", width: 120 },
  { key: "pmlinMioEur", labelKey: "ram.locationList.pmlinMioEur", width: 120 },
  { key: "emlinMioEur", labelKey: "ram.locationList.emlinMioEur", width: 120 },
  {
    key: "insurersRaiting",
    labelKey: "ram.locationList.insurersRating",
    width: 120,
  },
  {
    key: "reportCreatorCode",
    labelKey: "ram.reportList.reportCreatorCode",
    width: 150,
  },
  {
    key: "isReportDeactivated",
    labelKey: "ram.reportList.isActive",
    width: 100,
  },
];

export const Reports = () => {
  const { t } = useTranslation();
  const reports = useSelector(selectShownReports);
  const sort = useSelector((s) => s.reportsPage.sort);
  const dispatch: AppDispatch = useDispatch();
  const areReportsLoading = useSelector((s) => s.reports.isLoading);
  const taxonomy = useSelector((s) => s.taxonomy);
  const pasteDialog = useSelector(
    (s) => s.recommendationsPage.pasteRecommendationDialog,
  );
  const deleteDialog = useSelector((s) => s.reportsPage.deleteReportDialog);
  const changeLocationDialog = useSelector(
    (s) => s.reportsPage.changeLocationDialog,
  );
  const theme = useTheme() as IStyledTheme;
  const refExcelExport = useRef<ExcelExport>();

  // Column options (replaces old column visibility, filtering, and reordering)
  const columnOptionsTaxonomy = useMemo(() => {
    const result: { [key: string]: { code: string; id: number }[] } = {};
    for (const key of Object.keys(taxonomy)) {
      if (key === "response" || key === "isLoading" || key === "isLoadingUAD") continue;
      result[key] = (taxonomy as any)[key]?.items || [];
    }

    // Only show taxonomy values that exist in the current data
    const usedReportCreatorIds = new Set<number>();
    for (const report of reports || []) {
      if (!report) continue;
      if ((report as any).reportCreatorCode != null) usedReportCreatorIds.add((report as any).reportCreatorCode);
    }
    if (result.RiskReportCreator) {
      result.RiskReportCreator = result.RiskReportCreator.filter((t) => usedReportCreatorIds.has(t.id));
    }

    return result;
  }, [taxonomy, reports]);

  const columnOptionsDefaultColumns = useMemo(
    () => getColumnOptionsColumns(t),
    [t],
  );

  const {
    isPanelOpen,
    openPanel,
    closePanel: closeColumnOptions,
    columns: draftColumns,
    views,
    selectedView,
    isReady,
    isViewLoading,
    onClickApply,
    onChangeView,
    setFilter: setColumnOptionsFilter,
    onSelectAll,
    onDeselectAll,
    onReorderColumn,
    onToggleColumnVisibility,
    onClickDeleteView,
    onClickClearAllFilters,
    onClickReset,
    onClickSaveView,
    onCreateView,
    onUpdateView,
    onResetAllStorage,
    activeColumns,
    applyFiltersToData,
    activeFilterCount,
  } = useColumnOptionsState({
    tableId: "reports",
    defaultColumns: columnOptionsDefaultColumns,
    taxonomy: columnOptionsTaxonomy,
  });

  // Table sorting
  const { handleColumnClick, getSortIcon } = useTableSort({
    sort,
    setSort: (newSort) => dispatch(setSortReports(newSort)),
    defaultSortKey: "reportCreatedAt",
    defaultSortDir: "desc",
  });

  // Apply filters to reports data
  const filteredReports = useMemo(() => {
    return applyFiltersToData(reports || []) as Report[];
  }, [reports, applyFiltersToData]);

  const visibleLabelKeys = useMemo(
    () =>
      activeColumns
        .filter((c) => c.isVisible && c.key !== "options")
        .map((c) => {
          const def = defaultColumns.find((d: any) => d.key === c.key);
          return def?.labelKey || "";
        })
        .filter(Boolean),
    [activeColumns],
  );

  const excelColumns = useMemo(
    () =>
      allReportExcelColumns
        .filter((c) => visibleLabelKeys.includes(c.labelKey))
        .sort(
          (a, b) =>
            visibleLabelKeys.indexOf(a.labelKey) -
            visibleLabelKeys.indexOf(b.labelKey),
        ),
    [visibleLabelKeys],
  );

  const fileName = useMemo(() => {
    return "Reports" + moment(new Date()).format("_YYYY_MM_DD_HH_mm_ss");
  }, []);

  // Column widths persistence
  const defaultWidths = useMemo(
    () =>
      defaultColumns.reduce(
        (acc, col) => {
          acc[col.key] = col.minWidth;
          return acc;
        },
        {} as Record<string, number>,
      ),
    [],
  );
  const { columnWidths, updateColumnWidth } = useColumnWidths({
    storageKey: "reports-fluent",
    defaultWidths,
  });

  useEffect(() => {
    dispatch(loadReportsActions.trigger());
  }, [dispatch]);

  // Render cell content based on column
  const renderCell = useCallback(
    (item: Report, column: IColumn) => {
      const fieldName = column.fieldName as keyof Report;
      switch (column.key) {
        case "reportName":
          return (
            <TooltipComponent
              content={item?.reportName}
              overflowMode={TooltipOverflowMode.Parent}
            >
              {item?.reportName}
            </TooltipComponent>
          );
        case "options":
          return (
            <CenteredCell>
              <ContextButton report={item} />
            </CenteredCell>
          );
        case "surveyDate":
          return item?.surveyDate ? formatDateString(item.surveyDate) : "";
        case "pmlinMioEur":
        case "emlinMioEur":
          const value = item?.[fieldName];
          if (value !== null && value !== undefined) {
            return formatNumber(Number(value));
          }
          return "";
        case "reportCreatorCode":
          return t(
            taxonomy.RiskReportCreator.byId[item?.reportCreatorCode]?.code,
          );
        case "isReportDeactivated":
          return <ActivateDeactivateCell report={item} />;
        case "reportDocumentPath":
          if (item?.reportDocumentPath) {
            return (
              <CenteredCell>
                <ListContextMenuIconButton
                  iconProps={{ iconName: "Download" }}
                  onClick={() => window.open(item?.reportDocumentPath)}
                />
              </CenteredCell>
            );
          }
          return null;
        default:
          return item?.[fieldName] ?? "";
      }
    },
    [t, taxonomy],
  );

  // Build columns for Fluent UI DetailsList
  const detailsListColumns: IColumn[] = useMemo(() => {
    const activeVisibleKeys = activeColumns
      .filter((c) => c.isVisible)
      .map((c) => c.key);

    const defaultColumnsMap = new Map(
      defaultColumns.map((c: any) => [c.key, c]),
    );

    return activeVisibleKeys
      .map((key) => defaultColumnsMap.get(key))
      .filter(Boolean)
      .map((column: any) => {
        let iconName = column.iconName;
        if (column.key === sort.key && column.isSortable) {
          iconName = getSortIcon(column.key);
        }

        const currentWidth = columnWidths[column.key] || column.minWidth || 100;

        return {
          key: column.key,
          name: column.labelKey ? t(column.labelKey) : "",
          fieldName: column.fieldName,
          minWidth: column.isResizable
            ? Math.min(currentWidth, 50)
            : column.minWidth,
          maxWidth: column.isResizable ? 1000 : column.maxWidth,
          isResizable: column.isResizable,
          isSorted: sort.key === column.key,
          isSortedDescending: sort.key === column.key && sort.dir === "desc",
          iconName,
          onColumnClick: column.isSortable
            ? () => handleColumnClick(column.key, column.isSortable)
            : undefined,
          onRender: (item: Report) =>
            renderCell(item, {
              key: column.key,
              fieldName: column.fieldName,
            } as IColumn),
          ...(column.isResizable && {
            currentWidth,
            calculatedWidth: currentWidth,
          }),
        } as IColumn;
      });
  }, [t, sort, columnWidths, getSortIcon, handleColumnClick, renderCell, activeColumns]);

  const onColumnResize = useCallback(
    (column?: IColumn, newWidth?: number) => {
      if (column && newWidth) {
        updateColumnWidth(column.key, newWidth);
      }
    },
    [updateColumnWidth],
  );

  const onRenderDetailsHeader = (
    props: IDetailsHeaderProps | undefined,
    defaultRender: IRenderFunction<IDetailsHeaderProps> | undefined,
  ) => (
    <Sticky
      stickyPosition={StickyPositionType.Header}
      isScrollSynced
      stickyClassName={classes.stickyHeader}
    >
      {defaultRender!({
        ...props,
        styles: { root: { paddingTop: 0 } },
      })}
    </Sticky>
  );

  const onRenderRow = (props: any) => {
    return <DetailsRow {...props} />;
  };

  const classes = mergeStyleSets({
    container: {
      height: "calc(100vh - 94px)",
      position: "relative",
    },
    stickyHeader: {
      zIndex: 2,
    },
    table: {
      ".ms-DetailsHeader": {
        paddingTop: 0,
        borderBottom: `1px solid ${theme.isDark ? "#333" : "#edebe9"}`,
      },
      ".ms-DetailsHeader-cell": {
        height: "42px",
      },
      ".ms-DetailsRow": {
        borderBottom: `1px solid ${theme.isDark ? "#333" : "#edebe9"}`,
      },
      ".ms-DetailsRow-cell": {
        display: "flex",
        alignItems: "center",
        minHeight: "42px",
      },
    },
  });

  return (
    <>
      <Navigation
        refExcelExport={refExcelExport?.current}
        filteredReports={filteredReports}
        visibleLabelKeys={visibleLabelKeys}
        onOpenColumnOptions={openPanel}
        activeFilterCount={activeFilterCount}
        filteredCount={filteredReports.length}
      />
      {isPanelOpen && (
        <ColumnOptions
          closePanel={closeColumnOptions}
          columns={draftColumns}
          onClickApply={onClickApply}
          views={views}
          selectedView={selectedView}
          onChangeView={onChangeView}
          isReady={isReady}
          isViewLoading={isViewLoading}
          setFilter={setColumnOptionsFilter}
          onSelectAll={onSelectAll}
          onDeselectAll={onDeselectAll}
          taxonomy={columnOptionsTaxonomy}
          onClickDeleteView={onClickDeleteView}
          onClickClearAllFilters={onClickClearAllFilters}
          onClickReset={onClickReset}
          onClickSaveView={onClickSaveView}
          onCreateView={onCreateView}
          onUpdateView={onUpdateView}
          onReorderColumn={onReorderColumn}
          onToggleColumnVisibility={onToggleColumnVisibility}
          onResetAllStorage={onResetAllStorage}
          t={t}
        />
      )}
      <div className={classes.container}>
        <ScrollablePane
          styles={{
            stickyAbove: {
              zIndex: 3,
            },
          }}
        >
          <ShimmeredDetailsList
            items={filteredReports || []}
            compact={false}
            columns={detailsListColumns}
            selectionMode={SelectionMode.none}
            layoutMode={DetailsListLayoutMode.fixedColumns}
            constrainMode={ConstrainMode.unconstrained}
            enableShimmer={areReportsLoading}
            onRenderDetailsHeader={onRenderDetailsHeader}
            onRenderRow={onRenderRow}
            onColumnResize={onColumnResize}
            className={classes.table}
          />
        </ScrollablePane>
      </div>
      {deleteDialog.isOpen && <DeleteDialog />}
      {changeLocationDialog.isOpen && <ChangeLocationDialog />}
      {areReportsLoading && <TableSpinner />}
      {pasteDialog.isOpen && <PasteDialog />}
      <ExportExcelGeneric
        ref={refExcelExport}
        columns={excelColumns}
        fileName={fileName}
      />
    </>
  );
};
